/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Form, Input, InputNumber, Radio, Space } from "antd";
import { useEffect } from "react";
import { useAppSelector } from "../../../app/store";
import { useAddLeaveAutoProrateRunMutation } from "../../../features/leaveProrate/leaveProrateApi";
import {
  useAddLeaveTypeMutation,
  useGetLeaveTypeDetailsQuery,
  useUpdateLeaveTypeMutation,
} from "../../../features/leaveType/leaveTypeApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import { toast } from "../../../lib/toast";
import { ModalComponentProps } from "../../../types";
import FullScreenLoading from "../../common/FullScreenLoading";
import SuccessModal from "../../Report/SuccessModal";
import ConfirmModal from "../../ui/ConfirmModal";
import CustomModal from "../../ui/CustomModal";
import Loader from "../../ui/Loader";

export default function AddNewLeaveModal({
  handleClose,
  type,
  data,
  ...rest
}: ModalComponentProps<number | undefined>) {
  // const navigate = useNavigate();
  const [form] = Form.useForm();
  const { authInfo } = useAppSelector((state) => state.auth);
  const {
    open,
    handleOpen,
    handleClose: handleLeaveModalClose,
  } = useModal<undefined, "CONFIRM" | "SUCCESS">();
  const [
    updateLeaveType,
    {
      isSuccess: isUpdateSuccess,
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      error: updateError,
    },
  ] = useUpdateLeaveTypeMutation();
  const [addLeaveType, { isSuccess: isAddLeaveSuccess, isError, error }] =
    useAddLeaveTypeMutation();
  const {
    data: leaveDetails,
    isLoading: isDetailsLoading,
    isError: isDetailsError,
    error: detailsError,
  } = useGetLeaveTypeDetailsQuery(data, {
    skip: type === "ADD" || !data,
  });
  const [
    addLeaveAutoProrateRun,
    {
      isLoading: leaveAutoProrateRunLoading,
      isError: isAutoProrateError,
      error: autoProrateError,
      isSuccess: isAutoProrateSuccess,
      // data: autoProrateData,
    },
  ] = useAddLeaveAutoProrateRunMutation();

  useError(isError, error, "Leave Type added fail");
  useError(isUpdateError, updateError, "Leave Type updated fail");
  useError(isDetailsError, detailsError, "Get leave type fail");
  useError(isAutoProrateError, autoProrateError, "Prorate list fetched fail");

  useEffect(() => {
    if (type === "UPDATE") {
      form.setFieldsValue({
        title: leaveDetails?.data?.leave_type?.title,
        total_days: leaveDetails?.data?.leave_type?.total_days,
        is_half_day_enable: leaveDetails?.data?.leave_type?.is_half_day_enable,
      });
    }
  }, [form, leaveDetails?.data, type]);

  const onFinish = (values: any) => {
    const newData = {
      ...values,
    };

    if (type === "UPDATE") {
      updateLeaveType({
        id: data,
        data: {
          ...newData,
          is_published: leaveDetails?.data?.leave_type?.is_published ?? 1,
        },
      });
    } else {
      newData.business_id = authInfo?.business_id;
      newData.is_published = 1;
      addLeaveType(newData);
    }
  };

  useEffect(() => {
    if (isAddLeaveSuccess) {
      toast("success", "Leave Type added successfully");
      handleOpen({
        type: "CONFIRM",
      });
    }

    if (isUpdateSuccess) {
      toast("success", "Leave Type updated successfully");
      handleOpen({
        type: "CONFIRM",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateSuccess, isAddLeaveSuccess]);

  /**
   *  addLeaveAutoProrateRun({
        is_prorated: "yes",
        prorate_type: "leave_type_create",
      });
   */

  // success toast message
  useEffect(() => {
    if (isAutoProrateSuccess) {
      toast("success", "Prorate list fetched successfully");
      handleOpen({ type: "SUCCESS" });
      // if (autoProrateData?.data?.prorate_run?.status === "success") {
      //   navigate("/admin/task-queue");
      // }
    }
  }, [handleClose, handleLeaveModalClose, isAutoProrateSuccess]);

  useEffect(() => {
    if (isAutoProrateError || isAutoProrateSuccess) {
      handleLeaveModalClose();
      handleClose();
    }
  }, [
    handleClose,
    handleLeaveModalClose,
    isAutoProrateError,
    isAutoProrateSuccess,
  ]);

  return (
    <CustomModal
      title={type === "UPDATE" ? "Update Leave" : "Add New Leave"}
      {...rest}
      handleClose={handleClose}
      width={460}
      footer={null}
    >
      {!isAutoProrateError &&
        !isAutoProrateSuccess &&
        leaveAutoProrateRunLoading && <FullScreenLoading />}

      {open?.type === "SUCCESS" && (
        <SuccessModal
          type="queue"
          open={open?.open}
          handleClose={handleLeaveModalClose}
        />
      )}

      {open?.type === "CONFIRM" ? (
        <ConfirmModal
          open={open?.open}
          handleClose={() => {
            handleLeaveModalClose();
            handleClose();
          }}
          handleOk={() => {
            addLeaveAutoProrateRun({
              is_prorated: "yes",
              prorate_type:
                type === "UPDATE" ? "leave_type_update" : "leave_type_create",
            });
          }}
          confirmTitle="Run Auto Prorate"
          questionText="Are sure to run auto prorate?"
          okText="Confirm"
        />
      ) : null}

      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        {isDetailsLoading ? (
          <Loader />
        ) : (
          <>
            <Form.Item
              name="title"
              label="Leave Name"
              className="mb-4"
              rules={[
                {
                  required: true,
                  message: "Please enter leave name",
                },
              ]}
            >
              <Input size="large" placeholder="Enter leave name" />
            </Form.Item>
            <Form.Item
              name="total_days"
              label="Total Days"
              className="mb-4"
              validateFirst
              rules={[
                {
                  required: true,
                  message: "Please enter total days",
                },
                {
                  type: "integer",
                  message: "Total days cannot be decimal",
                },
                {
                  type: "number",
                  min: 1,
                  message: "Total days must be at least one",
                },
                {
                  type: "number",
                  max: 365,
                  message: "Please enter a maximum of 365 days",
                },
              ]}
            >
              <InputNumber
                type="number"
                size="large"
                placeholder="Ex: 1"
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Space className="w-full flex items-baseline" size={24}>
              <h6 className="text-header text-sm font-medium">Half Day:</h6>
              <Form.Item
                name="is_half_day_enable"
                rules={[
                  {
                    required: true,
                    message: "Please select a half-day option!",
                  },
                ]}
              >
                <Radio.Group className="custom-radio">
                  {halfDay?.map(({ id, label, value }) => (
                    <Radio value={value} key={id}>
                      {label}
                    </Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
            </Space>

            <Space className="w-full mt-2" size={12}>
              <Button
                className="normal-dark-button modal-button"
                onClick={handleClose}
                size="middle"
              >
                Cancel
              </Button>
              <Button
                className="modal-button"
                type="primary"
                htmlType="submit"
                size="middle"
                loading={isUpdateLoading}
              >
                {type === "UPDATE" ? "Update" : "Save"}
              </Button>
            </Space>
          </>
        )}
      </Form>
    </CustomModal>
  );
}

// const halfDay = ["1st Half", "2nd Half", "Deactivate"];
const halfDay = [
  {
    id: 1,
    label: "Active",
    value: 1,
  },
  {
    id: 2,
    label: "Inactive",
    value: 0,
  },
];
